import { ArrowLeftOutlined, CloseOutlined, CompressOutlined } from '@ant-design/icons';
import React, { Suspense, lazy, useCallback, useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { useTheme } from '../../contexts/ThemeContext';
import { useOptionalMediaViewer } from '../../contexts/MediaViewerContext';
import { trackChatFiles, viewerOrigin } from '../Chat/ChatFiles/chatFilesTracking';
import AudioPlayer from './components/AudioPlayer';
import DocumentViewer from './components/DocumentViewer/DocumentViewer';
import FileViewer from './components/FileViewer';
import ImageViewer from './components/ImageViewer';
import PDFViewer from './components/PDFViewer';
import MediaDownloadButton from './components/MediaDownloadButton';
import MediaInfoBar from './components/MediaInfoBar';
import MediaNavigation from './components/MediaNavigation';
import VideoPlayer from './components/VideoPlayer';
import UnsupportedFileView from './components/UnsupportedFileView';
import './FullPageMediaViewer.css';
import {
  detectMediaType,
  getMediaFileSize,
  getMediaTitle,
  getMediaUrl,
  normalizeMediaFiles
} from './utils/mediaTypeDetector';

// three.js (~1.4MB) loads only when a 3D file is actually opened — keep it out of the entry bundle.
const ThreeDViewer = lazy(() => import('./components/ThreeDViewer'));

const FullPageMediaViewer = (props) => {
  // Handle backward compatibility
  const {
    images,           // Old prop name
    mediaFiles,       // New prop name
    initialIndex = 0,
    onClose,
    onBack,           // Optional (the chat's viewer over the Files list, one viewer Oct 16 2026): "←" back to Files at
                      // this file. Renders a ← top left (the info pill moves right of it). Callers that omit it
                      // (posts, channels, task modal, avatars, any other chat file) render exactly as before.
    onMinimize,       // Optional (desktop chat viewer only): step down to the split panel.
                      // When provided, a Compress button renders next to ✕ and Esc steps down
                      // instead of closing. ✕ itself ALWAYS closes entirely (doctrine §1.4,
                      // right_panel_coherence_prd.md). Standalone callers (Posts, Avatars,
                      // TaskDetailModal, ChannelPage) omit it — behavior unchanged.
    mode = 'auto',
    // Issue 2: New props for context synchronization (optional)
    onNavigate,       // Callback to sync navigation with parent/context
    videoState,       // { timestamp, wasPlaying } from context
    onVideoStateChange, // Callback to update video state in context
    className = ''    // Optional extra class on the root — e.g. a z-index lift when the viewer
                      // opens above another modal (TaskDetailModal passes 'task-media-viewer')
  } = props;

  // Use images if mediaFiles not provided (backward compat)
  const media = mediaFiles || images;

  // If old 'images' prop used, force image mode
  const effectiveMode = images && !mediaFiles ? 'image' : mode;

  const { isDarkMode } = useTheme();
  // Chat Files analytics (CEL-3473 round 5): log the viewer's actions ONLY when this is the chat's full-screen viewer
  // showing a file of the Files list (ChatWindow passes the context's own mediaFiles array), opened from the gallery or
  // (one viewer, Oct 16 2026) from the chat; each event says which (`origin`). Any other caller (posts, channels, task
  // modal, avatars; inside the provider or not) logs nothing and behaves exactly as before.
  const chatViewer = useOptionalMediaViewer();
  const origin = chatViewer && chatViewer.mode === 'fullscreen' && media === chatViewer.mediaFiles
    ? viewerOrigin(chatViewer) : null;
  const trackGallery = useCallback((element, detail) => {
    if (origin) trackChatFiles(element, detail, undefined, { origin });
  }, [origin]);
  const [localIndex, setLocalIndex] = useState(initialIndex);
  const [touchStart, setTouchStart] = useState({ x: 0, y: 0 });
  
  // Video ref for state sync
  const videoRef = useRef(null);

  // Ref for boundary bounce animation (pure DOM — no React re-renders)
  const bounceRef = useRef(null);

  const triggerBounce = useCallback((direction) => {
    const el = bounceRef.current;
    if (!el) return;
    // Remove any existing bounce class, force reflow, re-add for restart
    el.classList.remove('boundary-bounce-left', 'boundary-bounce-right');
    void el.offsetWidth; // Force reflow to restart animation
    el.classList.add(`boundary-bounce-${direction}`);
    // Fallback cleanup for prefers-reduced-motion (animationend won't fire)
    setTimeout(() => {
      if (el) el.classList.remove('boundary-bounce-left', 'boundary-bounce-right');
    }, 350);
  }, []);

  // Use onNavigate if provided (context-synced), otherwise manage locally
  const currentIndex = onNavigate ? initialIndex : localIndex;

  // Normalize media files
  const normalizedMedia = normalizeMediaFiles(media);
  const currentMedia = normalizedMedia[currentIndex];
  const mediaType = effectiveMode === 'auto' ? detectMediaType(currentMedia) : effectiveMode;
  const currentUrl = getMediaUrl(currentMedia);
  const currentTitle = getMediaTitle(currentMedia);
  const currentFileSize = getMediaFileSize(currentMedia);

  // Get MIME type for unsupported file view
  const getMimeType = (media) => {
    if (typeof media === 'object' && media !== null) {
      return media.mimeType || media.mime_type || null;
    }
    return null;
  };

  // Theme class for components
  const themeClass = isDarkMode ? 'dark' : 'light';

  // Navigation functions — clamp at boundaries with bounce feedback
  // `how` (analytics only): click (the buttons pass their event), key or swipe.
  const goToPrevious = useCallback((e, how = 'click') => {
    if (e) e.stopPropagation();
    trackGallery('chatfiles_prev', how);

    if (onNavigate) {
      // Context-synced mode
      if (initialIndex <= 0) {
        triggerBounce('right');
        return;
      }
      onNavigate('prev');
    } else {
      // Local mode (Posts, Avatars, etc.)
      if (localIndex <= 0) {
        triggerBounce('right');
        return;
      }
      setLocalIndex(prev => prev - 1);
    }
  }, [normalizedMedia.length, onNavigate, initialIndex, localIndex, triggerBounce, trackGallery]);

  const goToNext = useCallback((e, how = 'click') => {
    if (e) e.stopPropagation();
    trackGallery('chatfiles_next', how);

    if (onNavigate) {
      // Context-synced mode
      if (initialIndex >= normalizedMedia.length - 1) {
        triggerBounce('left');
        return;
      }
      onNavigate('next');
    } else {
      // Local mode
      if (localIndex >= normalizedMedia.length - 1) {
        triggerBounce('left');
        return;
      }
      setLocalIndex(prev => prev + 1);
    }
  }, [normalizedMedia.length, onNavigate, initialIndex, localIndex, triggerBounce, trackGallery]);

  // A horizontal swipe that starts inside something that really scrolls sideways (a wide
  // markdown table, a code block) scrolls that element; it is never file navigation.
  const startsInSideScroller = (target, boundary) => {
    let el = target instanceof Element ? target : null;
    while (el && el !== boundary) {
      if (el.scrollWidth > el.clientWidth + 1) {
        const { overflowX } = window.getComputedStyle(el);
        if (overflowX === 'auto' || overflowX === 'scroll') return true;
      }
      el = el.parentElement;
    }
    return false;
  };

  // Touch handlers for mobile swipe navigation
  const handleTouchStart = (e) => {
    setTouchStart({
      x: e.touches[0].clientX,
      y: e.touches[0].clientY,
      inScroller: startsInSideScroller(e.target, e.currentTarget),
    });
  };

  const handleTouchEnd = (e) => {
    if (touchStart.inScroller) return;
    const touchEndX = e.changedTouches[0].clientX;
    const touchEndY = e.changedTouches[0].clientY;

    const deltaX = touchStart.x - touchEndX;
    const deltaY = touchStart.y - touchEndY;

    if (Math.abs(deltaX) > Math.abs(deltaY) && Math.abs(deltaX) > 50) {
      if (deltaX > 0) {
        goToNext(undefined, 'swipe');
      } else {
        goToPrevious(undefined, 'swipe');
      }
    }
  };

  // Handle close with video state sync
  const handleClose = useCallback(() => {
    // If we have a video and context sync, update the timestamp before closing
    if (videoRef.current && onVideoStateChange) {
      onVideoStateChange(videoRef.current.currentTime, !videoRef.current.paused);
    }
    onClose();
  }, [onClose, onVideoStateChange]);

  // Minimize (→ split panel) with the same video-state sync; only wired when onMinimize exists.
  const handleMinimize = useCallback(() => {
    if (!onMinimize) return;
    if (videoRef.current && onVideoStateChange) {
      onVideoStateChange(videoRef.current.currentTime, !videoRef.current.paused);
    }
    onMinimize();
  }, [onMinimize, onVideoStateChange]);

  // The same two, plus the gallery event: how = click (the buttons), key (Esc) or backdrop.
  const closeBy = useCallback((how) => {
    trackGallery('chatfiles_viewer_close', how);
    handleClose();
  }, [trackGallery, handleClose]);
  const minimizeBy = useCallback((how) => {
    trackGallery('chatfiles_minimize', how);
    handleMinimize();
  }, [trackGallery, handleMinimize]);
  // ← back to Files, with the same video-state sync as close.
  const backBy = (e) => {
    if (e) e.stopPropagation();
    trackGallery('chatfiles_back', 'click');
    if (videoRef.current && onVideoStateChange) {
      onVideoStateChange(videoRef.current.currentTime, !videoRef.current.paused);
    }
    onBack();
  };

  // A download inside the viewer (MediaDownloadButton, shared): seen on its way down, gallery files only.
  const onGalleryClickCapture = (e) => {
    if (e.target && e.target.closest && e.target.closest('.media-download-button')) {
      trackGallery('chatfiles_download', 'fullscreen');
    }
  };

  // Keyboard navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      // Don't interfere with video/audio player controls
      if ((mediaType === 'video' || mediaType === 'audio') &&
        (e.key === ' ' || e.key === 'f' || e.key === 'F' || e.key === 'm' || e.key === 'M' ||
          e.key === '+' || e.key === '-' || e.key === '=' || e.key === '_')) {
        return;
      }

      switch (e.key) {
        case 'Escape':
          // Doctrine Esc = the gentle step down (right_panel_coherence_prd.md §1.4): back to
          // the split when one exists (onMinimize provided), close entirely otherwise.
          if (onMinimize) minimizeBy('key');
          else closeBy('key');
          break;
        case 'ArrowLeft':
        case 'ArrowUp':
          goToPrevious(undefined, 'key');
          break;
        case 'ArrowRight':
        case 'ArrowDown':
          goToNext(undefined, 'key');
          break;
        default:
          break;
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [goToPrevious, goToNext, closeBy, minimizeBy, onMinimize, mediaType]);

  // Preload next media item
  useEffect(() => {
    if (currentIndex < normalizedMedia.length - 1) {
      const nextMedia = normalizedMedia[currentIndex + 1];
      const nextType = detectMediaType(nextMedia);
      const nextUrl = getMediaUrl(nextMedia);

      if (nextType === 'image' && nextUrl) {
        const img = new Image();
        img.src = nextUrl;
      } else if (nextType === 'video' && nextUrl) {
        // Create video element for metadata preload
        const video = document.createElement('video');
        video.preload = 'metadata';
        video.src = nextUrl;
      }
    }
  }, [currentIndex, normalizedMedia]);

  // Handle video time updates to sync with context
  const handleVideoTimeUpdate = useCallback((time) => {
    if (onVideoStateChange) {
      onVideoStateChange(time, true);
    }
  }, [onVideoStateChange]);

  // Full-bleed renderers (pdf / file / document / 3d) paint an opaque surface,
  // so unlike images/videos they can't float under the top chrome (info pill,
  // counter, download, close). This wrapper class reserves the top strip for
  // the chrome. The multi-file counter pill lives in that same top strip, so
  // no bottom reservation is needed — the bottom edge belongs to content.
  const insetSurfaceClass = 'media-viewer-inset-surface';

  // Render appropriate viewer based on media type
  const renderMediaContent = () => {
    switch (mediaType) {
      case 'image':
        return (
          <>
            <MediaInfoBar 
              filename={currentTitle} 
              fileSize={currentFileSize} 
              isDarkMode={isDarkMode} 
            />
            <MediaDownloadButton 
              url={currentUrl} 
              filename={currentTitle} 
              isDarkMode={isDarkMode} 
            />
            <ImageViewer
              src={currentUrl}
              filename={currentTitle}
              fileSize={currentFileSize}
              isDarkMode={isDarkMode}
            />
          </>
        );

      case 'video':
        return (
          <>
            <MediaInfoBar 
              filename={currentTitle} 
              fileSize={currentFileSize} 
              isDarkMode={isDarkMode} 
            />
            <MediaDownloadButton 
              url={currentUrl} 
              filename={currentTitle} 
              isDarkMode={isDarkMode} 
            />
            <VideoPlayer
              src={currentUrl}
              filename={currentTitle}
              fileSize={currentFileSize}
              isDarkMode={isDarkMode}
              videoRef={videoRef}
              initialTimestamp={videoState?.timestamp || 0}
              autoPlay={videoState?.wasPlaying || false}  // Fix 1: Pass autoPlay from context
              onTimeUpdate={handleVideoTimeUpdate}
            />
          </>
        );

      case 'audio':
        return (
          <>
            <MediaInfoBar 
              filename={currentTitle} 
              fileSize={currentFileSize} 
              isDarkMode={isDarkMode} 
            />
            <MediaDownloadButton 
              url={currentUrl} 
              filename={currentTitle} 
              isDarkMode={isDarkMode} 
            />
            <AudioPlayer
              src={currentUrl}
              title={currentTitle}
              filename={currentTitle}
              fileSize={currentFileSize}
              isDarkMode={isDarkMode}
              audioRef={videoRef}
              initialTimestamp={videoState?.timestamp || 0}
              autoPlay={videoState?.wasPlaying || false}
              onTimeUpdate={handleVideoTimeUpdate}
            />
          </>
        );

      case 'pdf':
        return (
          <>
            <MediaInfoBar 
              filename={currentTitle} 
              fileSize={currentFileSize} 
              isDarkMode={isDarkMode} 
            />
            <MediaDownloadButton 
              url={currentUrl} 
              filename={currentTitle} 
              isDarkMode={isDarkMode} 
            />
            <div className={`${insetSurfaceClass} media-viewer-doc-surface`}>
              <PDFViewer
                url={currentUrl}
                filename={currentTitle}
                fileSize={currentFileSize}
                isDarkMode={isDarkMode}
              />
            </div>
          </>
        );

      case 'markdown':
      case 'html':
      case 'code':
      case 'csv':
        return (
          <>
            <MediaInfoBar
              filename={currentTitle}
              fileSize={currentFileSize}
              isDarkMode={isDarkMode}
            />
            <MediaDownloadButton
              url={currentUrl}
              filename={currentTitle}
              isDarkMode={isDarkMode}
            />
            <div className={`${insetSurfaceClass} media-viewer-doc-surface`}>
              <FileViewer
                url={currentUrl}
                filename={currentTitle}
                fileSize={currentFileSize}
                isDarkMode={isDarkMode}
                fileType={mediaType}
              />
            </div>
          </>
        );

      case 'document':
        return (
          <>
            <MediaInfoBar
              filename={currentTitle}
              fileSize={currentFileSize}
              isDarkMode={isDarkMode}
            />
            <MediaDownloadButton
              url={currentUrl}
              filename={currentTitle}
              isDarkMode={isDarkMode}
            />
            <div className={insetSurfaceClass}>
              <DocumentViewer
                key={currentUrl}
                url={currentUrl}
                filename={currentTitle}
                fileSize={currentFileSize}
                isDarkMode={isDarkMode}
                mimeType={getMimeType(currentMedia)}
              />
            </div>
          </>
        );

      case 'threed':
        return (
          <>
            <MediaInfoBar 
              filename={currentTitle} 
              fileSize={currentFileSize} 
              isDarkMode={isDarkMode} 
            />
            <MediaDownloadButton 
              url={currentUrl} 
              filename={currentTitle} 
              isDarkMode={isDarkMode} 
            />
            <div className={insetSurfaceClass}>
              <Suspense fallback={<div style={{ padding: 24, textAlign: 'center', opacity: 0.6 }}>Loading 3D viewer…</div>}>
                <ThreeDViewer
                  key={currentUrl}
                  src={currentUrl}
                  filename={currentTitle}
                  fileSize={currentFileSize}
                  isDarkMode={isDarkMode}
                />
              </Suspense>
            </div>
          </>
        );

      default:
        // Use UnsupportedFileView for consistency
        return (
          <>
            <MediaInfoBar 
              filename={currentTitle} 
              fileSize={currentFileSize} 
              isDarkMode={isDarkMode} 
            />
            <MediaDownloadButton 
              url={currentUrl} 
              filename={currentTitle} 
              isDarkMode={isDarkMode} 
            />
            <UnsupportedFileView
              filename={currentTitle}
              fileSize={currentFileSize}
              mimeType={getMimeType(currentMedia)}
              url={currentUrl}
              isDarkMode={isDarkMode}
            />
          </>
        );
    }
  };

  return ReactDOM.createPortal(
    <div className={`full-page-media-viewer ${className}`.trim()}>
      {/* Backdrop that closes on click */}
      <div
        className={`media-viewer-backdrop ${themeClass}`}
        onClick={() => closeBy('backdrop')}
      />

      {/* Content that doesn't close on click */}
      <div
        className={`media-viewer-content${onMinimize ? ' has-minimize' : ''}${onBack ? ' has-back' : ''}`}
        onTouchStart={handleTouchStart}
        onTouchEnd={handleTouchEnd}
        onClickCapture={origin ? onGalleryClickCapture : undefined}
      >
        {/* Inner wrapper for boundary bounce animation — only content bounces, not nav/close */}
        <div
          ref={bounceRef}
          className="media-viewer-content-inner"
          onAnimationEnd={(e) => {
            if (e.animationName.startsWith('boundary-bounce-')) {
              e.currentTarget.classList.remove('boundary-bounce-left', 'boundary-bounce-right');
            }
          }}
        >
          {renderMediaContent()}
        </div>

        <MediaNavigation
          currentIndex={currentIndex}
          totalItems={normalizedMedia.length}
          onPrevious={goToPrevious}
          onNext={goToNext}
          isDarkMode={isDarkMode}
        />

        {onBack && (
          <button
            className={`media-viewer-close media-viewer-back ${themeClass}`}
            onClick={backBy}
            aria-label="Back to files"
            title="Back to files"
          >
            <ArrowLeftOutlined />
          </button>
        )}
        {onMinimize && (
          <button
            className={`media-viewer-close media-viewer-minimize ${themeClass}`}
            onClick={() => minimizeBy('click')}
            aria-label="Minimize to panel"
            title="Minimize to panel (Esc)"
          >
            <CompressOutlined />
          </button>
        )}
        <button
          className={`media-viewer-close ${themeClass}`}
          onClick={() => closeBy('click')}
          aria-label="Close viewer"
          title="Close"
        >
          <CloseOutlined />
        </button>
      </div>
    </div>,
    document.body
  );
};

export default FullPageMediaViewer;
